import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { PULSE_KIND_AR, NAME_POLICY_AR } from "@/v2/lib/rbac";
import { GlassCard, Chip, Skeleton, Modal, Confirm, Toggle, Table, Pagination, PageHead } from "@/v2/admin/ui";
import { pulseIcon } from "@/v2/components/PulseBar";
import {
  Activity, Plus, Pin, PinOff, Eye, EyeOff, Trash2, Save, Check, X, ArrowUp, ArrowDown, Megaphone,
} from "lucide-react";

const ICON_CHOICES = ["Megaphone", "Sparkles", "Trophy", "Award", "Star", "Flame", "Gem", "Rocket", "Zap", "Building2"];

function ManualModal({ onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ text: "", icon: "Megaphone", priority: 85, pinned: false, starts_at: "", ends_at: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.text.trim()) return toast.error("نص الرسالة مطلوب");
    setSaving(true);
    try {
      await api.post("/admin/pulse", {
        ...f, priority: Number(f.priority),
        starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null,
        ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null,
      });
      toast.success("تمت إضافة الرسالة إلى نبض التعلّم");
      qc.invalidateQueries({ queryKey: ["adm-pulse"] });
      qc.invalidateQueries({ queryKey: ["pulse"] });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحفظ"); } finally { setSaving(false); }
  };
  return (
    <Modal title="إضافة رسالة تحفيزية" onClose={onClose} testId="pulse-manual-modal">
      <div className="space-y-3">
        <div>
          <label className="v2-label">نص الرسالة</label>
          <textarea rows={2} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })}
            data-testid="pulse-manual-text" className="v2-input resize-none"
            placeholder="🎉 أسبوع جديد مليء بالتحديات والإنجازات — بالتوفيق للجميع" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="v2-label">الأيقونة</label>
            <select value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} data-testid="pulse-manual-icon" className="v2-input">
              {ICON_CHOICES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="v2-label">أولوية الظهور (0-100)</label>
            <input type="number" min="0" max="100" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}
              data-testid="pulse-manual-priority" className="v2-input" />
          </div>
          <div>
            <label className="v2-label">تاريخ البداية (اختياري)</label>
            <input type="datetime-local" dir="ltr" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })}
              data-testid="pulse-manual-start" className="v2-input" />
          </div>
          <div>
            <label className="v2-label">تاريخ النهاية (اختياري)</label>
            <input type="datetime-local" dir="ltr" value={f.ends_at} onChange={(e) => setF({ ...f, ends_at: e.target.value })}
              data-testid="pulse-manual-end" className="v2-input" />
          </div>
        </div>
        <Toggle checked={f.pinned} onChange={(v) => setF({ ...f, pinned: v })} label="تثبيت الرسالة في مقدمة الشريط" testId="pulse-manual-pinned" />
      </div>
      <button onClick={save} disabled={saving} data-testid="pulse-manual-save" className="v2-btn w-full mt-5">
        {saving ? "جارٍ الحفظ..." : "إضافة الرسالة"}
      </button>
    </Modal>
  );
}

export default function AdminPulse() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [manual, setManual] = useState(false);
  const [delId, setDelId] = useState(null);
  const [form, setForm] = useState(null);

  const { data: settings } = useQuery({ queryKey: ["adm-pulse-settings"], queryFn: async () => (await api.get("/admin/pulse/settings")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["adm-pulse", page, kind, status],
    queryFn: async () => (await api.get(`/admin/pulse?page=${page}&limit=20&kind=${kind}&status=${status}`)).data,
  });
  const { data: preview } = useQuery({ queryKey: ["pulse"], queryFn: async () => (await api.get("/pulse")).data });

  const s = form || settings;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["adm-pulse"] });
    qc.invalidateQueries({ queryKey: ["pulse"] });
    qc.invalidateQueries({ queryKey: ["pulse-list"] });
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/pulse/settings", {
        enabled: s.enabled, speed: Number(s.speed), item_duration_days: Number(s.item_duration_days),
        max_items: Number(s.max_items), name_policy: s.name_policy, allowed_kinds: s.allowed_kinds,
        celebration_enabled: s.celebration_enabled, suggestions_enabled: s.suggestions_enabled,
        cheer_enabled: s.cheer_enabled,
      });
      toast.success("تم حفظ إعدادات نبض التعلّم");
      setForm(null);
      qc.invalidateQueries({ queryKey: ["adm-pulse-settings"] });
      refresh();
    } catch { toast.error("تعذّر الحفظ"); }
  };

  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/pulse/${id}`, body); toast.success(msg); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر التنفيذ"); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/pulse/${delId}`); toast.success("تم حذف الخبر"); refresh(); }
    catch { toast.error("تعذّر الحذف"); }
    setDelId(null);
  };
  const pinnedRows = (data?.rows || []).filter((r) => r.pinned);
  const movePinned = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= pinnedRows.length) return;
    const ids = pinnedRows.map((r) => r.id);
    [ids[idx], ids[t]] = [ids[t], ids[idx]];
    try { await api.post("/admin/pulse/reorder", { ids }); refresh(); }
    catch { toast.error("تعذّر إعادة الترتيب"); }
  };

  if (!s) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-5" data-testid="admin-pulse">
      <PageHead icon={Activity} title="إدارة نبض التعلّم" sub="شريط عام موحّد لكل المنصة — إنجازات إيجابية فقط.">
        <button onClick={() => setManual(true)} data-testid="pulse-add-manual" className="v2-btn v2-btn-sm">
          <Plus size={15} /> رسالة تحفيزية
        </button>
      </PageHead>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6 items-start">
        <GlassCard gold className="p-5 space-y-3">
          <h3 className="font-bold">الإعدادات العامة</h3>
          <Toggle checked={s.enabled} onChange={(v) => setForm({ ...s, enabled: v })} label="تشغيل الشريط" testId="pulse-enabled" />
          <Toggle checked={s.celebration_enabled} onChange={(v) => setForm({ ...s, celebration_enabled: v })} label="لحظات الاحتفال (Celebration)" testId="pulse-celebration" />
          <Toggle checked={s.cheer_enabled} onChange={(v) => setForm({ ...s, cheer_enabled: v })} label="أزرار التهنئة والتشجيع" testId="pulse-cheer-toggle" />
          <Toggle checked={s.suggestions_enabled} onChange={(v) => setForm({ ...s, suggestions_enabled: v })} label="السماح لمديري الإدارات باقتراح إنجازات" testId="pulse-suggestions" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="v2-label">سرعة الحركة (ثانية للدورة)</label>
              <input type="number" min="15" max="180" value={s.speed} onChange={(e) => setForm({ ...s, speed: e.target.value })} data-testid="pulse-speed" className="v2-input" />
            </div>
            <div>
              <label className="v2-label">عدد الأخبار المعروضة</label>
              <input type="number" min="4" max="40" value={s.max_items} onChange={(e) => setForm({ ...s, max_items: e.target.value })} data-testid="pulse-max" className="v2-input" />
            </div>
          </div>
          <div>
            <label className="v2-label">مدة ظهور الخبر (أيام)</label>
            <input type="number" min="1" max="90" value={s.item_duration_days} onChange={(e) => setForm({ ...s, item_duration_days: e.target.value })} data-testid="pulse-duration" className="v2-input" />
          </div>
          <div>
            <label className="v2-label">سياسة عرض الأسماء</label>
            <select value={s.name_policy} onChange={(e) => setForm({ ...s, name_policy: e.target.value })} data-testid="pulse-name-policy" className="v2-input">
              {Object.entries(NAME_POLICY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="v2-label">أنواع الإنجازات التي تظهر</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PULSE_KIND_AR).map(([k, label]) => {
                const on = (s.allowed_kinds || []).includes(k);
                return (
                  <button key={k} data-testid={`pulse-kind-${k}`}
                    onClick={() => setForm({
                      ...s,
                      allowed_kinds: on ? s.allowed_kinds.filter((x) => x !== k) : [...(s.allowed_kinds || []), k],
                    })}
                    className={`px-3 h-8 rounded-full text-[11px] font-bold ${on ? "bg-[#91cab8] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={saveSettings} data-testid="pulse-save-settings" className="v2-btn w-full !h-11"><Save size={15} /> حفظ الإعدادات</button>
        </GlassCard>

        <div className="space-y-4">
          <GlassCard className="p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Eye size={16} className="text-[#e3c799]" /> معاينة الشريط</h3>
            <div className="v2-inset p-3 space-y-2 max-h-52 overflow-y-auto" data-testid="pulse-preview">
              {(preview?.items || []).slice(0, 8).map((it) => {
                const I = pulseIcon(it.icon);
                return (
                  <div key={it.id} className="flex items-center gap-2 text-sm">
                    <span className="w-7 h-7 rounded-full bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center shrink-0"><I size={13} /></span>
                    <span className="text-[#f6f8fa]/85">{it.text}</span>
                  </div>
                );
              })}
              {!(preview?.items || []).length && <p className="text-sm text-[#bcc8cf]">لا توجد أخبار حاليًا.</p>}
            </div>
          </GlassCard>

          <div className="flex flex-wrap gap-2 items-center">
            <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} data-testid="pulse-filter-kind" className="v2-input !w-auto text-sm font-bold">
              <option value="">كل الأنواع</option>
              {Object.entries(PULSE_KIND_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="pulse-filter-status" className="v2-input !w-auto text-sm font-bold">
              <option value="">كل الحالات</option>
              <option value="approved">معتمد</option>
              <option value="pending">بانتظار الاعتماد</option>
              <option value="rejected">مرفوض</option>
            </select>
            {data?.pending > 0 && (
              <button onClick={() => { setStatus("pending"); setPage(1); }} data-testid="pulse-pending-badge" className="v2-btn-ghost v2-btn-sm">
                <Megaphone size={14} /> {data.pending} اقتراح بانتظار المراجعة
              </button>
            )}
          </div>

          {pinnedRows.length > 1 && (
            <GlassCard className="p-4">
              <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Pin size={14} className="text-[#e3c799]" /> ترتيب الأخبار المثبتة</h4>
              <div className="space-y-2">
                {pinnedRows.map((r, i) => (
                  <div key={r.id} className="flex items-center gap-2 v2-inset p-2 text-xs">
                    <span className="flex-1 truncate">{r.preview}</span>
                    <button onClick={() => movePinned(i, -1)} disabled={i === 0} data-testid={`pulse-pin-up-${i}`} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowUp size={12} /></button>
                    <button onClick={() => movePinned(i, 1)} disabled={i === pinnedRows.length - 1} className="w-7 h-7 rounded-lg v2-inset flex items-center justify-center disabled:opacity-30"><ArrowDown size={12} /></button>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {isLoading ? <Skeleton className="h-72 w-full" /> : (
            <Table head={["الخبر", "النوع", "المصدر", "الحالة", "التاريخ", "إجراءات"]} min={860} testId="pulse-table">
              {(data?.rows || []).map((r, i) => (
                <tr key={r.id} data-testid={`pulse-event-${i}`}>
                  <td className="max-w-[320px] truncate">{r.preview}</td>
                  <td><Chip tone="muted">{r.kind_label}</Chip></td>
                  <td className="text-[11px] text-[#bcc8cf]">
                    {r.source === "auto" ? "تلقائي" : r.source === "manual" ? "يدوي" : `اقتراح — ${r.meta?.suggested_by || ""}`}
                  </td>
                  <td>
                    <Chip tone={r.status === "approved" ? "emerald" : r.status === "pending" ? "gold" : "red"}>
                      {r.status === "approved" ? "معتمد" : r.status === "pending" ? "بانتظار" : "مرفوض"}
                    </Chip>
                    {r.hidden && <Chip tone="red">مخفي</Chip>}
                  </td>
                  <td className="text-[11px] text-[#bcc8cf]" dir="ltr">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>
                    <div className="flex gap-1.5">
                      {r.status === "pending" && (
                        <>
                          <button onClick={() => patch(r.id, { status: "approved" }, "تم اعتماد الاقتراح")} data-testid={`pulse-approve-${i}`}
                            className="w-8 h-8 rounded-lg bg-[#91cab8]/20 text-[#91cab8] flex items-center justify-center"><Check size={14} /></button>
                          <button onClick={() => patch(r.id, { status: "rejected" }, "تم رفض الاقتراح")} data-testid={`pulse-reject-${i}`}
                            className="w-8 h-8 rounded-lg bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center"><X size={14} /></button>
                        </>
                      )}
                      <button onClick={() => patch(r.id, { pinned: !r.pinned }, r.pinned ? "تم إلغاء التثبيت" : "تم تثبيت الخبر")} data-testid={`pulse-pin-${i}`}
                        className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center">{r.pinned ? <PinOff size={14} /> : <Pin size={14} />}</button>
                      <button onClick={() => patch(r.id, { hidden: !r.hidden }, r.hidden ? "تم إظهار الخبر" : "تم إخفاء الخبر")} data-testid={`pulse-hide-${i}`}
                        className="w-8 h-8 rounded-lg v2-inset flex items-center justify-center">{r.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                      <button onClick={() => setDelId(r.id)} data-testid={`pulse-del-${i}`}
                        className="w-8 h-8 rounded-lg bg-[#B45309]/20 text-[#F7A08A] flex items-center justify-center"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
        </div>
      </div>

      {manual && <ManualModal onClose={() => setManual(false)} />}
      {delId && <Confirm title="حذف الخبر" danger confirmLabel="نعم، احذف"
        body="سيُحذف هذا الخبر من نبض التعلّم نهائيًا وتُسجَّل العملية في سجل الإدارة."
        onConfirm={remove} onClose={() => setDelId(null)} testId="pulse-del-confirm" />}
    </div>
  );
}
